import { ORDER_QUEUE_DEBOUNCE_INTERVAL_IN_MS } from "@/store/appConstants";

/**
 * OrderSyncCoordinator (Phase 3)
 * ------------------------------
 * A single, debounced coordinator for persisting the active order. It replaces
 * the four overlapping ad-hoc debouncers that previously lived across the order
 * screens and the order mixin:
 *
 *   1. orderMixin.scheduleOrderSync   (pendingItemSyncTimeout, 1000ms)
 *   2. view.scheduleQueueSync         (queueSyncTimeout, 3000ms)
 *   3. the deep `currentOrder` watcher (which fanned out to scheduleQueueSync)
 *   4. view.forceQueueSync            (immediate flush on leave / unload)
 *
 * It exposes ONE pipeline with four explicit, ordered stages:
 *
 *   localPersist  -> dispatch('orders/saveOrder')       (commits the pure
 *                                                         mutation, then the
 *                                                         action performs the
 *                                                         IndexedDB + Ably I/O)
 *   enqueue       -> dispatch('orders/enqueueOrderSync') (durable outbox)
 *   drain         -> dispatch('orders/drainOrderSyncOutbox')
 *
 * The local persist stage always runs. create-blank is dispatched once from
 * initNewOrder (or the selectCustomer / table-order creation paths), not here.
 * The queue stages
 * (enqueue + drain) only run when a caller asks for them (`queue: true`). The
 * coordinator owns both the local debounce and the queue debounce so all four
 * legacy timers funnel through one object with one `schedule()` / `flush()`
 * API. The two debounces are independent (queueing does not delay the faster
 * local persist), matching the previous two-timer latency exactly.
 *
 * The coordinator is intentionally framework-agnostic (no Vue/`this`): callers
 * pass the Vuex `store`, a `getOrder()` accessor for the active order, and
 * optional hooks. This keeps it pure enough to unit test with a mock store.
 *
 * @param {object}   opts
 * @param {object}   opts.store        Vuex store
 * @param {Function} opts.getOrder     () => active order object (or null)
 * @param {Function} [opts.prepareOrder] hook run right before localPersist
 *                                       (e.g. line-item normalization)
 * @param {Function} [opts.onPersisted] hook run after localPersist (order)
 * @param {number}   [opts.localDelayMs=1000]
 * @param {number}   [opts.queueDelayMs=ORDER_QUEUE_DEBOUNCE_INTERVAL_IN_MS]
 * @param {Function} [opts.isOnline]   () => boolean (defaults to navigator.onLine)
 */
export function createOrderSyncCoordinator(opts = {}) {
  const {
    store,
    getOrder,
    prepareOrder,
    onPersisted,
    localDelayMs = 1000,
    queueDelayMs = ORDER_QUEUE_DEBOUNCE_INTERVAL_IN_MS,
    isOnline,
  } = opts;

  if (!store || typeof getOrder !== "function") {
    throw new Error("[OrderSyncCoordinator] `store` and `getOrder` are required");
  }

  let localTimer = null;
  let queueTimer = null;
  let queueTrigger = "order_sync";

  // ── Stage 1: local persist ───────────────────────────────────────────────
  async function runLocal() {
    if (typeof prepareOrder === "function") prepareOrder();
    const order = getOrder();
    if (!order) return null;

    store.dispatch("orders/saveOrder", order);
    if (typeof onPersisted === "function") onPersisted(order);
    return order;
  }

  // Resolve the freshest copy of the order from the store, preferring one that
  // has a server id (mirrors the old waitForOrderIdBeforeQueueSync).
  function resolveForQueue(order) {
    if (!order) return null;
    const key = order.id || order.local_id;
    if (!key) return order;
    const fromStore = store.getters["orders/getOrderByIdOrLocalId"](key);
    return fromStore || order;
  }

  // ── Stage 3+4: enqueue + drain ───────────────────────────────────────────
  async function runQueue(trigger) {
    await runLocal();
    let order = resolveForQueue(getOrder());
    if (!order || (!order.id && !order.local_id)) return;

    const online = typeof isOnline === "function" ? isOnline() : navigator.onLine;
    try {
      await store.dispatch("orders/enqueueOrderSync", { order, trigger });
      if (online) {
        await store.dispatch("orders/drainOrderSyncOutbox");
      }
    } catch (e) {
      console.warn("[OrderSyncCoordinator] queue sync failed:", e);
    }
  }

  function clearLocalTimer() {
    if (localTimer) {
      clearTimeout(localTimer);
      localTimer = null;
    }
  }

  function clearQueueTimer() {
    if (queueTimer) {
      clearTimeout(queueTimer);
      queueTimer = null;
    }
  }

  async function fireLocal() {
    localTimer = null;
    try {
      await runLocal();
    } catch (e) {
      console.error("[OrderSyncCoordinator] scheduled local sync error:", e);
    }
  }

  async function fireQueue() {
    const trigger = queueTrigger;
    queueTimer = null;
    // The queue pipeline persists locally first, so a separately pending local
    // debounce would be redundant work — collapse it.
    clearLocalTimer();
    try {
      await runQueue(trigger);
    } catch (e) {
      console.error("[OrderSyncCoordinator] scheduled queue sync error:", e);
    }
  }

  return {
    /**
     * Debounced schedule. Call this at every real mutation point (add item,
     * modifier save, discount change, payment, etc.).
     *
     * @param {object}  [options]
     * @param {boolean} [options.queue=false] also enqueue + drain to backend
     * @param {string}  [options.trigger]     outbox trigger label
     */
    schedule({ queue = false, trigger = "order_sync" } = {}) {
      if (queue) {
        queueTrigger = trigger;
        clearQueueTimer();
        queueTimer = setTimeout(fireQueue, queueDelayMs);
      } else {
        clearLocalTimer();
        localTimer = setTimeout(fireLocal, localDelayMs);
      }
    },

    /**
     * Run a stage immediately, cancelling any pending debounce. Used on order
     * leave / page unload (replaces forceQueueSync) and anywhere a synchronous
     * persist is required.
     *
     * @param {object}  [options]
     * @param {boolean} [options.queue=true]
     * @param {string}  [options.trigger]
     */
    async flush({ queue = true, trigger = "order_force_sync" } = {}) {
      clearLocalTimer();
      if (queue) {
        clearQueueTimer();
        await runQueue(trigger);
      } else {
        await runLocal();
      }
    },

    /** Cancel any pending debounced syncs without running them. */
    cancel() {
      clearLocalTimer();
      clearQueueTimer();
    },

    /** Test/introspection helper. */
    hasPending() {
      return localTimer !== null || queueTimer !== null;
    },
  };
}

export default createOrderSyncCoordinator;
